import { Button, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import React, { memo, useEffect } from 'react';
import { PopupComponent } from 'src/components/common/PopupComponent';
import { useUser } from 'src/components/hooks/useUser';
import { archLevel } from 'src/data/config';
import { hideLoader, showLoader } from 'src/store/Loader';
import { setMessage } from 'src/store/Message';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { useProtocolDeviationsLog } from './useProtocolDeviationsLog';

const DeleteProtocolDeviation: React.FunctionComponent<any> = ({
  openDelete,
  setOpenDelete,
  deleteData,
  deviationLevel,
  pageSize,
  setPageNumber
}) => {
  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const dispatch = useAppDispatch();
  const { user } = useUser();
  const {
    getProtocolDeviationLogData,
    deleteProtocolDeviationsLog,
    deleteProtocolDeviations,
    deleteProtocolDeviationsIsLoading,
    deleteProtocolDeviationsIsError,
    deleteProtocolDeviationsError,
    deleteProtocolDeviationsIsSuccess,
    deleteAllProtocolDeviationsLog,
    deleteAllProtocolDeviations,
    deleteAllProtocolDeviationsIsLoading,
    deleteAllProtocolDeviationsIsError,
    deleteAllProtocolDeviationsError,
    deleteAllProtocolDeviationsIsSuccess
  } = useProtocolDeviationsLog();

  useEffect(() => {
    if (deleteProtocolDeviationsIsLoading || deleteAllProtocolDeviationsIsLoading) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }
  }, [deleteProtocolDeviationsIsLoading, deleteAllProtocolDeviationsIsLoading]);

  useEffect(() => {
    if (deleteProtocolDeviationsIsSuccess || deleteAllProtocolDeviationsIsSuccess) {
      setPageNumber(0);
      getProtocolDeviationLogData({
        site_id: selectedSite,
        study_id: studyId,
        deviation_level: deviationLevel,
        country: selectedCountry,
        application: archLevel == 'site' ? process.env.REACT_APP_TSDA_SLUG : process.env.REACT_APP_NAME,
        from: 0,
        size: pageSize
      });
      setOpenDelete(false);
      dispatch(
        setMessage({
          msgDetails: 'Protocol deviation deleted successfully',
          severity: 'success'
        })
      );
    }
  }, [
    deleteProtocolDeviationsIsSuccess,
    deleteAllProtocolDeviationsIsSuccess,
    deleteProtocolDeviations,
    deleteAllProtocolDeviations
  ]);

  useEffect(() => {
    if (deleteProtocolDeviationsIsError || deleteAllProtocolDeviationsIsError) {
      const err: any = deleteProtocolDeviationsError || deleteAllProtocolDeviationsError;
      if (err?.status == 500) {
        dispatch(
          setMessage({
            msgDetails: `${err?.status} : Internal Server Error`,
            severity: 'error'
          })
        );
      } else if (err?.status == 403) {
        dispatch(
          setMessage({
            msgDetails: 'Error 403 Restricted User permission',
            severity: 'error'
          })
        );
      } else if (err?.status == 404) {
        dispatch(
          setMessage({
            msgDetails: 'Error in deleteing protocol deviation',
            severity: 'error'
          })
        );
      }
    }
  }, [deleteProtocolDeviationsIsError, deleteAllProtocolDeviationsIsError]);

  const handleDeleteCancel = () => {
    setOpenDelete(false);
  };

  const handleDelete = () => {
    if (deleteData?.created_by === user) {
      deleteProtocolDeviationsLog({
        id: deleteData.id,
        deviation_level: deviationLevel,
        site_id: selectedSite,
        study_id: studyId,
        country: selectedCountry,
        application: 'CTMS'
      });
    } else {
      deleteAllProtocolDeviationsLog({
        id: deleteData.id,
        site_id: selectedSite,
        study_id: studyId,
        country: selectedCountry,
        application: 'CTMS',
        deviation_level: deleteData.deviation_level
      });
    }
  };

  return (
    <PopupComponent aria-labelledby="customized-dialog-title" open={openDelete} maxWidth="lg">
      <DialogTitle>Delete Protocol Deviation?</DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          Are you sure you want to delete the {deleteData?.deviation_no} protocol deviation?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button className="grey-btn" onClick={handleDeleteCancel}>
          No
        </Button>
        <Button onClick={handleDelete}>Yes</Button>
      </DialogActions>
    </PopupComponent>
  );
};

export default memo(DeleteProtocolDeviation);
